export type FiltersMultiValue = Array<string | number>;

/** Runtime + serializable filter values (dates as unix seconds, DatePickerV2-compatible). */
export type FiltersValues = {
  status: string[];
  workStartDateFrom: number | null;
  workStartDateTo: number | null;
  organizationId: FiltersMultiValue;
  blockId: FiltersMultiValue;
  fieldId: FiltersMultiValue;
  padId: FiltersMultiValue;
  wellId: FiltersMultiValue;
  rigModelId: FiltersMultiValue;
  brigadeId: FiltersMultiValue;
  /** Common_WellboreTypeInstance ids — interpreted by consumers (e.g. reference-wells). */
  wellboreTypeInstanceId: FiltersMultiValue;
};

export type FiltersValuesSerializable = FiltersValues;

export type FiltersWidgetStateParams = {
  values?: FiltersValuesSerializable;
};

export const EMPTY_FILTERS_VALUES: FiltersValues = {
  status: [],
  workStartDateFrom: null,
  workStartDateTo: null,
  organizationId: [],
  blockId: [],
  fieldId: [],
  padId: [],
  wellId: [],
  rigModelId: [],
  brigadeId: [],
  wellboreTypeInstanceId: [],
};

export function copyFiltersValues(values: FiltersValues): FiltersValues {
  return {
    status: [...values.status],
    workStartDateFrom: values.workStartDateFrom,
    workStartDateTo: values.workStartDateTo,
    organizationId: [...values.organizationId],
    blockId: [...values.blockId],
    fieldId: [...values.fieldId],
    padId: [...values.padId],
    wellId: [...values.wellId],
    rigModelId: [...values.rigModelId],
    brigadeId: [...values.brigadeId],
    wellboreTypeInstanceId: [...values.wellboreTypeInstanceId],
  };
}

export function serializeFiltersValues(values: FiltersValues): FiltersValuesSerializable {
  return copyFiltersValues(values);
}

export function deserializeFiltersValues(values?: FiltersValuesSerializable | null): FiltersValues {
  if (!values) {
    return copyFiltersValues(EMPTY_FILTERS_VALUES);
  }

  return {
    status: Array.isArray(values.status) ? [...values.status] : [],
    workStartDateFrom: typeof values.workStartDateFrom === 'number' ? values.workStartDateFrom : null,
    workStartDateTo: typeof values.workStartDateTo === 'number' ? values.workStartDateTo : null,
    organizationId: Array.isArray(values.organizationId) ? [...values.organizationId] : [],
    blockId: Array.isArray(values.blockId) ? [...values.blockId] : [],
    fieldId: Array.isArray(values.fieldId) ? [...values.fieldId] : [],
    padId: Array.isArray(values.padId) ? [...values.padId] : [],
    wellId: Array.isArray(values.wellId) ? [...values.wellId] : [],
    rigModelId: Array.isArray(values.rigModelId) ? [...values.rigModelId] : [],
    brigadeId: Array.isArray(values.brigadeId) ? [...values.brigadeId] : [],
    wellboreTypeInstanceId: Array.isArray(values.wellboreTypeInstanceId) ? [...values.wellboreTypeInstanceId] : [],
  };
}

function isSameMultiValue(a: FiltersMultiValue, b: FiltersMultiValue): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

export function areFiltersValuesEqual(a: FiltersValues, b: FiltersValues): boolean {
  return (
    isSameMultiValue(a.status, b.status) &&
    a.workStartDateFrom === b.workStartDateFrom &&
    a.workStartDateTo === b.workStartDateTo &&
    isSameMultiValue(a.organizationId, b.organizationId) &&
    isSameMultiValue(a.blockId, b.blockId) &&
    isSameMultiValue(a.fieldId, b.fieldId) &&
    isSameMultiValue(a.padId, b.padId) &&
    isSameMultiValue(a.wellId, b.wellId) &&
    isSameMultiValue(a.rigModelId, b.rigModelId) &&
    isSameMultiValue(a.brigadeId, b.brigadeId) &&
    isSameMultiValue(a.wellboreTypeInstanceId, b.wellboreTypeInstanceId)
  );
}

export function isFiltersValuesEmpty(values: FiltersValues): boolean {
  return areFiltersValuesEqual(values, EMPTY_FILTERS_VALUES);
}
